import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  from,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import {
  COLLECTION_IDEMPOTENCY_OPERATION,
  CollectionIdempotentOperation,
} from '../idempotency/collection-request-idempotency';
import { CollectionRequestIdempotencyService } from '../idempotency/collection-request-idempotency.service';
import {
  EntityOwnerWaiverDocumentDto,
  EntityOwnerWaiverDto,
  EntityOwnerWaiverResponse,
  GenerateEntityOwnerWaiverBatchRequest,
  SignEntityOwnerWaiverBatchRequest,
} from '../interfaces/collection-revision-waiver.interface';
import { CollectionRevisionTargetRouteIdentity } from '../collection-flow/collection-revision-route';
import {
  BusinessEntity,
  BusinessEntityResponse,
} from '../interfaces/collection.interface';
import { withCollectionRequestContext } from '../interceptors/collection-request-context';
import { ConsentEvidenceService } from './certificate/consent-evidence.service';
import {
  buildCollectionRevisionTargetEntityWaiverBatchSignUrl,
  buildCollectionRevisionTargetEntityWaiverBatchesUrl,
  buildCollectionRevisionTargetEntityWaiverUrl,
} from './collection-revision-urls';
import { CollectionService } from './collection.service';
import { CollectionRevisionWireContractError } from './collection-revision.service';

@Injectable({
  providedIn: 'root',
})
export class WaiverService {
  private readonly http = inject(HttpClient);
  private readonly collectionService = inject(CollectionService);
  private readonly consentEvidenceService = inject(ConsentEvidenceService);
  private readonly idempotency = inject(CollectionRequestIdempotencyService);

  private normalizeBusinessEntity(
    response: BusinessEntityResponse,
  ): BusinessEntity {
    const entity = response.data;

    return {
      id: entity.id,
      document_number: entity.documentNumber,
      legal_name: entity.name,
      trade_name: entity.tradeName,
      opening_date: entity.company.openingDate,
      company_type: entity.company.companyType,
      company_size: entity.company.companySize,
      legal_nature: entity.company.legalNature,
      capital_social: entity.company.capitalSocial,
      situation: entity.company.status,
      primary_phone: entity.contact.primaryPhone,
      primary_email: entity.contact.primaryEmail,
      primary_address: {
        logradouro: entity.contact.primaryAddress.logradouro,
        numero: entity.contact.primaryAddress.numero,
        complemento: entity.contact.primaryAddress.complemento ?? undefined,
        bairro: entity.contact.primaryAddress.bairro,
        cidade: entity.contact.primaryAddress.cidade,
        uf: entity.contact.primaryAddress.uf,
        cep: entity.contact.primaryAddress.cep,
      },
      representatives: entity.representatives.map((representative, index) => ({
        id: `${representative.name}|${representative.role}|${index}`,
        name: representative.name,
        role: representative.role,
        document_number: representative.cpf ?? null,
      })),
      api_source: entity.sources.apiSource,
    };
  }

  getEntityOwnerWaiver(
    identity: CollectionRevisionTargetRouteIdentity,
    entityId: string,
  ): Observable<EntityOwnerWaiverDto> {
    const exactIdentity = requireTargetIdentity(identity, entityId);
    return this.http
      .get<EntityOwnerWaiverResponse>(
        buildCollectionRevisionTargetEntityWaiverUrl(
          `${this.collectionService.getApiUrl()}/v1`,
          exactIdentity,
          exactIdentity.entityId,
        ),
        { context: withCollectionRequestContext(exactIdentity.collectionId) },
      )
      .pipe(map((response) => decodeEntityOwnerWaiverResponse(response, exactIdentity)));
  }

  generateWaiverBatch(
    identity: CollectionRevisionTargetRouteIdentity,
    entityId: string,
    request: GenerateEntityOwnerWaiverBatchRequest,
  ): Observable<EntityOwnerWaiverDto> {
    const exactIdentity = requireTargetIdentity(identity, entityId);
    const operation = {
      kind: 'waiver-batch-generate',
      ...exactIdentity,
      waiverType: request.waiver_type,
    } as const;
    return this.decodeMutation(
      operation,
      this.http.post<EntityOwnerWaiverResponse>(
        buildCollectionRevisionTargetEntityWaiverBatchesUrl(
          `${this.collectionService.getApiUrl()}/v1`,
          exactIdentity,
          exactIdentity.entityId,
        ),
        request,
        { context: idempotentContext(operation) },
      ),
      exactIdentity,
    );
  }

  signWaiverBatch(
    identity: CollectionRevisionTargetRouteIdentity,
    entityId: string,
    waiverBatchId: string,
    waivers: readonly EntityOwnerWaiverDocumentDto[],
  ): Observable<EntityOwnerWaiverDto> {
    const exactIdentity = requireTargetIdentity(identity, entityId);
    const exactBatchId = requireId(waiverBatchId, 'waiverBatchId');
    const operationIds = waivers.map((waiver) => waiver.operation_id);
    if (operationIds.length === 0 || operationIds.some((id) => !id.trim())) {
      throw new TypeError('waivers must identify at least one exact operation');
    }
    const operation = {
      kind: 'waiver-batch-sign',
      ...exactIdentity,
      waiverBatchId: exactBatchId,
    } as const;

    return from(
      this.consentEvidenceService.generateConsentCertificate({
        collection_id: exactIdentity.collectionId,
        contract_ids: operationIds,
      }),
    ).pipe(
      switchMap((consentProof) => {
        const request: SignEntityOwnerWaiverBatchRequest = {
          payload: consentProof.payload,
          signature: consentProof.signature,
          public_key: consentProof.public_key,
        };
        return this.decodeMutation(
          operation,
          this.http.post<EntityOwnerWaiverResponse>(
            buildCollectionRevisionTargetEntityWaiverBatchSignUrl(
              `${this.collectionService.getApiUrl()}/v1`,
              exactIdentity,
              exactIdentity.entityId,
              exactBatchId,
            ),
            request,
            { context: idempotentContext(operation) },
          ),
          exactIdentity,
        );
      }),
    );
  }

  /**
   * GET /v1/entities/businesses/{businessId}
   * Fetch business entity details including representatives
   */
  getBusinessEntity(
    collectionId: string,
    businessId: string,
  ): Observable<BusinessEntity> {
    const url = `${this.collectionService.getApiUrl()}/v1/entities/businesses/${businessId}`;
    return this.http
      .get<BusinessEntityResponse>(url, {
        context: withCollectionRequestContext(collectionId),
      })
      .pipe(map((response) => this.normalizeBusinessEntity(response)));
  }

  private decodeMutation(
    operation: CollectionIdempotentOperation,
    request: Observable<EntityOwnerWaiverResponse>,
    identity: CollectionRevisionTargetRouteIdentity & { readonly entityId: string },
  ): Observable<EntityOwnerWaiverDto> {
    return request.pipe(
      map((response) => decodeEntityOwnerWaiverResponse(response, identity)),
      tap(() => this.idempotency.clear(operation)),
      catchError((error: unknown) => {
        if (isDefinitiveFailure(error)) this.idempotency.clear(operation);
        return throwError(() => error);
      }),
    );
  }
}

function idempotentContext(operation: CollectionIdempotentOperation) {
  return withCollectionRequestContext(operation.collectionId).set(
    COLLECTION_IDEMPOTENCY_OPERATION,
    operation,
  );
}

function requireTargetIdentity(
  identity: CollectionRevisionTargetRouteIdentity,
  entityId: string,
): CollectionRevisionTargetRouteIdentity & { readonly entityId: string } {
  return {
    collectionId: requireId(identity.collectionId, 'collectionId'),
    revisionId: requireId(identity.revisionId, 'revisionId'),
    targetId: requireId(identity.targetId, 'targetId'),
    entityId: requireId(entityId, 'entityId'),
  };
}

function decodeEntityOwnerWaiverResponse(
  response: EntityOwnerWaiverResponse,
  expected: CollectionRevisionTargetRouteIdentity & { readonly entityId: string },
): EntityOwnerWaiverDto {
  const waiverKeys = new Set([
    'collection_id',
    'revision_id',
    'target_id',
    'entity_id',
    'required',
    'status',
    'validity_days',
    'waiver_batch_id',
    'allowed_actions',
    'documents',
  ]);
  if (!isClosedRecord(response, new Set(['waiver']))) {
    throw invalidWaiverResponse();
  }
  const waiver = response.waiver;
  if (
    !isClosedRecord(waiver, waiverKeys) ||
    waiver.collection_id !== expected.collectionId ||
    waiver.revision_id !== expected.revisionId ||
    waiver.target_id !== expected.targetId ||
    waiver.entity_id !== expected.entityId ||
    typeof waiver.required !== 'boolean' ||
    !['NOT_REQUIRED', 'MISSING', 'AWAITING_SIGNATURE', 'ACTIVE', 'EXPIRED'].includes(
      waiver.status,
    ) ||
    (waiver.validity_days !== null && typeof waiver.validity_days !== 'number') ||
    (waiver.waiver_batch_id !== null &&
      (typeof waiver.waiver_batch_id !== 'string' || !waiver.waiver_batch_id.trim())) ||
    !Array.isArray(waiver.allowed_actions) ||
    waiver.allowed_actions.some(
      (action) => action !== 'GENERATE_BATCH' && action !== 'SIGN_BATCH',
    ) ||
    !Array.isArray(waiver.documents) ||
    waiver.documents.some((document) => !isWaiverDocument(document))
  ) {
    throw invalidWaiverResponse();
  }
  return waiver;
}

function isClosedRecord(
  value: unknown,
  allowedKeys: ReadonlySet<string>,
): value is Record<string, unknown> {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).every((key) => allowedKeys.has(key))
  );
}

function invalidWaiverResponse(): CollectionRevisionWireContractError {
  return new CollectionRevisionWireContractError(
    'Entity-owner waiver response is invalid.',
  );
}

function isWaiverDocument(value: unknown): value is EntityOwnerWaiverDocumentDto {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const document = value as Record<string, unknown>;
  const keys = Object.keys(document);
  const allowed = new Set([
    'operation_id',
    'operation_type',
    'document_url',
    'generated_at',
    'signed_at',
    'expires_at',
  ]);
  return (
    keys.every((key) => allowed.has(key)) &&
    ['operation_id', 'operation_type', 'document_url', 'generated_at'].every(
      (key) => typeof document[key] === 'string' && !!String(document[key]).trim(),
    ) &&
    ['signed_at', 'expires_at'].every(
      (key) => document[key] === null || typeof document[key] === 'string',
    )
  );
}

function requireId(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function isDefinitiveFailure(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return true;
  return !(error.status === 0 || error.status === 408 || error.status >= 500);
}
